import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { JADE_DARK, JADE_FONTS } from '../context/ThemeContext';
import axios from 'axios';

const backendApi = axios.create({ baseURL: `${process.env.REACT_APP_API_URL}/api` });

const { colors: C, nerveRgb } = JADE_DARK;
const F = JADE_FONTS;

/* ─── SVG Icons ─────────────────────────────────────────────────────────── */
function IconTrending() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}
function IconSparkle() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function IconBarChart() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
      <path d="M1 4L3.5 6.5L9 1" stroke={C.actionHi} strokeWidth="1.75"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── ToS Content ───────────────────────────────────────────────────────── */
const TOS_CONTENT = `Terms of Service

Last updated: April 29, 2026

1. Acceptance of Terms
By accessing or using MarketNerve ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.

2. Description of Service
MarketNerve provides financial market data, AI-powered stock analysis, quantitative signals, and macro insights for informational purposes only. The Service is intended for educational and research use.

3. Not Financial Advice
All content provided by MarketNerve, including but not limited to stock analysis, AI insights, quant signals, and macro data, is for informational purposes only and does not constitute financial, investment, legal, or tax advice. Always consult a qualified financial advisor before making investment decisions.

4. User Accounts
You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.

5. Acceptable Use
You agree not to: (a) use the Service for any unlawful purpose; (b) attempt to gain unauthorized access to any part of the Service; (c) redistribute or resell market data obtained through the Service; (d) reverse engineer or attempt to extract source code from the Service.

6. Data and Privacy
We collect and process personal data as described in our Privacy Policy. By using the Service, you consent to such collection and processing.

7. Market Data
Market data is sourced from third-party providers and may be delayed. MarketNerve makes no warranty as to the accuracy, timeliness, or completeness of any market data or analysis provided.

8. Limitation of Liability
To the maximum extent permitted by law, MarketNerve shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.

9. Changes to Terms
We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.

10. Contact
For questions about these Terms, contact us at support@marketnerve.io`;

/* ─── ToS Dialog (nested over the login card) ──────────────────────────── */
function TosDialog({ onClose }) {
  const closeRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => { closeRef.current?.focus(); }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const focusables = closeRef.current?.closest('[role="dialog"]')
          ?.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <motion.div
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.18 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      aria-hidden="true"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tos-title"
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, y: reduceMotion ? 0 : 16, scale: reduceMotion ? 1 : 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.16, 1, 0.3, 1] }}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: '1.25rem',
          width: '100%',
          maxWidth: '540px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
          outline: 'none',
          fontFamily: F.body,
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.125rem 1.5rem',
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
        }}>
          <h2
            id="tos-title"
            style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: C.action, letterSpacing: '-0.02em', fontFamily: F.display }}
          >
            Terms of Service
          </h2>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Close Terms of Service"
            className="mn-btn-close"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid transparent',
              borderRadius: '0.5rem',
              color: C.textMuted,
              cursor: 'pointer',
              fontSize: '1rem',
              width: '32px',
              height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s, color 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = C.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = C.textMuted; }}
          >
            <IconClose />
          </button>
        </div>

        <div
          tabIndex={0}
          style={{
            overflowY: 'auto',
            padding: '1.5rem',
            flex: 1,
            color: C.textMuted,
            fontSize: '0.8125rem',
            lineHeight: 1.9,
            whiteSpace: 'pre-wrap',
            scrollbarWidth: 'thin',
            scrollbarColor: `${C.border} transparent`,
            outline: 'none',
          }}
        >
          {TOS_CONTENT}
        </div>

        <div style={{
          padding: '1rem 1.5rem',
          borderTop: `1px solid ${C.border}`,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              background: C.action,
              border: 'none',
              borderRadius: '0.625rem',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 700,
              padding: '0.625rem 1.5rem',
              transition: 'opacity 0.15s',
              letterSpacing: '-0.01em',
              fontFamily: F.display,
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.82'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Got it
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Sign-in Modal ─────────────────────────────────────────────────────
   Pops up as a centered card over whatever page triggered it (landing,
   pricing, mentorship, or a protected-route redirect) instead of routing
   to a dedicated /login page. */
export default function LoginModal({ onClose, redirectTo = '/dashboard' }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [error, setError] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTos, setShowTos] = useState(false);
  const [tosShake, setTosShake] = useState(false);
  const checkboxRef = useRef(null);
  const closeBtnRef = useRef(null);
  const cardRef = useRef(null);

  // Lock background scroll while the modal is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Focus the close button on open; Escape to close; trap Tab inside the card
  useEffect(() => {
    closeBtnRef.current?.focus();
    const handleKeyDown = (e) => {
      if (showTos) return; // ToS dialog owns Escape/Tab while open
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Tab') {
        const focusables = cardRef.current?.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, showTos]);

  useEffect(() => {
    if (!tosShake) return;
    const t = setTimeout(() => setTosShake(false), 600);
    return () => clearTimeout(t);
  }, [tosShake]);

  const handleSuccess = async (credentialResponse) => {
    if (!tosAccepted) {
      setTosShake(true);
      setError('Please accept the Terms of Service to continue.');
      checkboxRef.current?.focus();
      return;
    }
    setError('');
    try {
      const { data } = await backendApi.post('/auth/google', {
        credential: credentialResponse.credential,
      });
      localStorage.setItem('mn_token', data.token);
      setUser(data.user);
      onClose();
      navigate(redirectTo);
    } catch {
      setError('Sign-in failed. Please try again.');
    }
  };

  const features = [
    { Icon: IconTrending, label: 'Live Nifty 50 & S&P 500 data' },
    { Icon: IconSparkle, label: 'AI-powered stock analysis' },
    { Icon: IconBarChart, label: 'Quant indicators & macro insights' },
  ];

  return (
    <AnimatePresence>
      <style>{`
        @keyframes mn-shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-6px)}
          40%{transform:translateX(6px)}
          60%{transform:translateX(-4px)}
          80%{transform:translateX(4px)}
        }
        @media (prefers-reduced-motion: reduce) {
          .mn-shake { animation: none !important; }
        }
        .mn-shake { animation: mn-shake 0.5s ease; }

        .mn-focus-ring:focus-visible {
          outline: 2px solid ${C.action};
          outline-offset: 2px;
        }
        .mn-btn-close:focus-visible {
          outline: 2px solid ${C.action};
          outline-offset: 2px;
          border-radius: 0.5rem;
        }
        .mn-checkbox:focus-visible + .mn-checkbox-box {
          outline: 2px solid ${C.action};
          outline-offset: 2px;
        }
      `}</style>

      {/* Backdrop */}
      <motion.div
        key="login-backdrop"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.18 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 'clamp(1rem, 4vw, 2rem)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          overflowY: 'auto',
        }}
      >
        {/* Card */}
        <motion.div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
          onClick={e => e.stopPropagation()}
          initial={{ opacity: 0, y: reduceMotion ? 0 : 24, scale: reduceMotion ? 1 : 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: reduceMotion ? 0 : 12, scale: reduceMotion ? 1 : 0.97 }}
          transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.16, 1, 0.3, 1] }}
          style={{
            width: '100%', maxWidth: '420px', position: 'relative',
            fontFamily: F.body, margin: 'auto',
          }}
        >
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: '1.5rem',
            padding: 'clamp(1.75rem, 5vw, 2.75rem) clamp(1.5rem, 5vw, 2.5rem)',
            boxShadow: `0 30px 90px rgba(0,0,0,0.6), inset 0 1px 0 rgba(${nerveRgb},0.06)`,
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Top accent hairline — the same gold-gradient seam used on the
                landing page's final-CTA card and the Pro pricing card, so the
                sign-in card reads as part of the same system rather than a
                bolted-on dialog. */}
            <div style={{
              position: 'absolute', top: 0, left: '15%', right: '15%', height: '2px',
              background: `linear-gradient(90deg, transparent, ${nerveRgb ? `rgba(${nerveRgb},0.7)` : C.action}, transparent)`,
              pointerEvents: 'none',
            }} />

            {/* Ambient glow, contained to the card */}
            <div style={{
              position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
              width: '340px', height: '220px',
              background: `radial-gradient(ellipse, rgba(${nerveRgb},0.14) 0%, transparent 70%)`,
              pointerEvents: 'none', borderRadius: 'inherit',
            }} />

            {/* Close button */}
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label="Close sign-in"
              className="mn-btn-close"
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid transparent',
                borderRadius: '0.5rem',
                color: C.textMuted,
                cursor: 'pointer',
                width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, color 0.15s',
                zIndex: 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = C.text; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = C.textMuted; }}
            >
              <IconClose />
            </button>

            {/* Logo + Brand */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.75rem', gap: '0.75rem', position: 'relative' }}>
              <div style={{
                width: '60px', height: '60px',
                borderRadius: '14px',
                overflow: 'hidden',
                border: `1.5px solid ${C.borderHi}`,
                boxShadow: `0 0 0 4px rgba(${nerveRgb},0.08), 0 10px 28px rgba(0,0,0,0.45)`,
                flexShrink: 0,
                background: C.bg,
              }}>
                <img
                  src="/logo.png"
                  alt="MarketNerve logo"
                  width="60"
                  height="60"
                  style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <h1
                  id="login-modal-title"
                  style={{
                    fontSize: '1.4rem', fontWeight: 800, margin: '0 0 0.3rem',
                    color: C.action, letterSpacing: '-0.035em', lineHeight: 1, fontFamily: F.display,
                  }}
                >
                  MarketNerve
                </h1>
                <p style={{
                  margin: 0, fontSize: '0.68rem', color: C.textDim,
                  fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: F.display,
                }}>
                  Market Intelligence Platform
                </p>
              </div>
            </div>

            {/* Features */}
            <div style={{
              background: `rgba(${nerveRgb},0.05)`,
              border: `1px solid ${C.border}`,
              borderRadius: '0.875rem',
              padding: '0.75rem 1.125rem',
              marginBottom: '1.5rem',
            }}>
              {features.map(({ Icon, label }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: '0.625rem',
                  padding: '0.3rem 0', fontSize: '0.8rem', color: C.textMuted,
                }}>
                  <span style={{ color: C.action, display: 'flex', flexShrink: 0 }}>
                    <Icon />
                  </span>
                  {label}
                </div>
              ))}
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.25rem' }}>
              <div style={{ flex: 1, height: '1px', background: C.border }} />
              <span style={{
                color: C.textMuted, fontSize: '0.66rem', fontWeight: 600,
                whiteSpace: 'nowrap', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: F.display,
              }}>
                Sign in to continue
              </span>
              <div style={{ flex: 1, height: '1px', background: C.border }} />
            </div>

            {/* ToS Checkbox row */}
            <div
              className={tosShake ? 'mn-shake' : ''}
              style={{ marginBottom: '1.125rem' }}
            >
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
                cursor: 'pointer',
                minHeight: '44px',
                paddingTop: '0.25rem',
              }}>
                <input
                  ref={checkboxRef}
                  type="checkbox"
                  className="mn-checkbox"
                  checked={tosAccepted}
                  onChange={e => {
                    setTosAccepted(e.target.checked);
                    if (e.target.checked) setError('');
                  }}
                  aria-label="I agree to the Terms of Service"
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '1px',
                    height: '1px',
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                  }}
                />
                <span
                  className="mn-checkbox-box"
                  aria-hidden="true"
                  style={{
                    width: '17px', height: '17px',
                    borderRadius: '4px',
                    border: `1.5px solid ${tosAccepted ? C.action : C.border}`,
                    background: tosAccepted ? `rgba(${nerveRgb},0.18)` : 'transparent',
                    flexShrink: 0,
                    marginTop: '2px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 0.15s, background 0.15s',
                    pointerEvents: 'none',
                  }}
                >
                  {tosAccepted && <IconCheck />}
                </span>

                <span style={{ fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.65 }}>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={e => { e.preventDefault(); setShowTos(true); }}
                    className="mn-focus-ring"
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      color: C.action, cursor: 'pointer', fontSize: '0.75rem',
                      fontWeight: 600, textDecoration: 'underline',
                      textUnderlineOffset: '2px', fontFamily: 'inherit',
                    }}
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <Link
                    to="/disclaimer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mn-focus-ring"
                    style={{
                      color: C.action, fontWeight: 600, textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    Disclaimer
                  </Link>
                  {' '}— market data is for informational purposes only, not financial advice.
                </span>
              </label>
            </div>

            {/* Google Sign-in — blocked until ToS accepted */}
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: '0.25rem' }}>
              <div style={{ opacity: tosAccepted ? 1 : 0.4, transition: 'opacity 0.2s', pointerEvents: tosAccepted ? 'auto' : 'none' }}>
                <GoogleLogin
                  onSuccess={handleSuccess}
                  onError={() => setError('Sign-in failed. Please try again.')}
                  theme="filled_black"
                  size="large"
                  shape="pill"
                  text="signin_with"
                  width="320"
                />
              </div>
              {!tosAccepted && (
                <div
                  aria-hidden="true"
                  onClick={() => { setTosShake(true); setError('Please accept the Terms of Service to continue.'); checkboxRef.current?.focus(); }}
                  style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
                />
              )}
            </div>

            <p style={{
              textAlign: 'center', color: C.textDim, fontSize: '0.68rem',
              margin: '0.75rem 0 0', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '0.375rem', opacity: 0.75,
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secured by Google — we never see your password
            </p>

            {error && (
              <motion.div
                role="alert"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.2 }}
                style={{
                  marginTop: '0.75rem', padding: '0.75rem 1rem',
                  background: 'rgba(212,86,58,0.1)',
                  border: `1px solid ${C.bear}55`,
                  borderRadius: '0.625rem',
                  color: C.bear, fontSize: '0.8rem', textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                {error}
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {showTos && <TosDialog key="tos-dialog" onClose={() => setShowTos(false)} />}
    </AnimatePresence>
  );
}
